import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid, Skeleton } from '@mui/material';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CerebroApiEndpoints } from '../shared/utils/api';
import { Urls, useAuth } from '../shared/context';
import { DateUtils, NumberUtils } from '../shared/utils';
import { useDealQuery, useCompanyNdaQuery, useNavigateToNextPage } from '../shared/hooks';
import { ReactComponent as BorrowingCapacity } from '../shared/icons/BorrowingCapacity.svg';
import { ReactComponent as ExpectedTermsAndRates } from '../shared/icons/ExpectedTermsAndRates.svg';
import { ReactComponent as AvailableLendingOptions } from '../shared/icons/AvailableLenderOptions.svg';
import {
	CerebroBreadcrumb,
	CerebroHeading,
	CerebroPageContainer,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
	CerebroStyledBox,
	CerebroText,
	CerebroLinkButton,
} from '../shared/components';
import { AssociateCompanyNda, SignNdaDrawer, UploadNdaDrawer } from './Nda';

export const Introduction: React.FC = () => {
	const [signNda, setSignNda] = React.useState(false);
	const [uploadNda, setUploadNda] = React.useState(false);
	const [associateNda, setAssociateNda] = React.useState(false);

	const { currentUser } = useAuth();
	const { guid } = useParams<'guid'>();
	const navigate = useNavigate();
	const navigateToNextPage = useNavigateToNextPage();

	const dealQuery = useDealQuery(guid);
	const ndaQuery = useCompanyNdaQuery(guid, {
		enabled: dealQuery.isSuccess,
		retry: false,
	});
	const isQueriesLoading = dealQuery.isLoading || ndaQuery.isLoading;

	const goToSummary = (): void => {
		navigate(`/${guid}/${Urls.Results.Root}`);
	};

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Introduction</CerebroBreadcrumb>
			<CerebroHeading size="h1" component="h1" sx={{ marginBottom: 3 }}>
				Welcome to Cerebro Capital
			</CerebroHeading>
			<CerebroText sx={{ marginBottom: 3 }}>
				This is your loan application. It has been saved based on what you have provided us.
			</CerebroText>

			{dealQuery.isLoading && <Skeleton height={400} />}
			{dealQuery.isSuccess && (
				<>
					<CerebroText sx={{ fontWeight: 'bold' }}>
						{dealQuery.data.companyName} - Loan Request for{' '}
						{NumberUtils.FormatCurrency(dealQuery.data.totalRequestedAmount)}
					</CerebroText>
				</>
			)}

			{dealQuery.isSuccess && dealQuery.data?.dealStatus !== 'Started' && (
				<>
					<Grid container justifyContent={{ xs: 'center', md: 'flex-start' }} sx={{ marginTop: 3 }}>
						<Grid item>
							<CerebroPrimaryButton onClick={() => goToSummary()}>Summary</CerebroPrimaryButton>
						</Grid>
					</Grid>
				</>
			)}

			{dealQuery.isSuccess && dealQuery.data?.dealStatus === 'Started' && (
				<>
					<CerebroText sx={{ marginTop: 3, marginBottom: 3 }}>
						Step through this easy wizard to continue.
					</CerebroText>
					<Grid container justifyContent={{ xs: 'center', md: 'flex-start' }}>
						<Grid item>
							<CerebroPrimaryButton onClick={() => navigateToNextPage()}>Continue</CerebroPrimaryButton>
						</Grid>
					</Grid>
				</>
			)}

			<CerebroText sx={{ marginBottom: 3, marginTop: 3 }}>
				Cerebro&apos;s data-driven marketplace will help you find the options that best fit your unique
				financing needs quickly &amp; easily. All lenders on our platform use this common application, so you
				can start matching and discovering your loan options immediately - all in one place.
			</CerebroText>

			<Grid container spacing={3}>
				<Grid item xs={4} sx={{ textAlign: 'center' }}>
					<AvailableLendingOptions />
					<CerebroText>Match to Top Lenders</CerebroText>
				</Grid>
				<Grid item xs={4} sx={{ textAlign: 'center' }}>
					<BorrowingCapacity />
					<CerebroText>Calculate Debt Capacity</CerebroText>
				</Grid>
				<Grid item xs={4} sx={{ textAlign: 'center' }}>
					<ExpectedTermsAndRates />
					<CerebroText>Compare Terms &amp; Rates</CerebroText>
				</Grid>
			</Grid>

			{isQueriesLoading && <Skeleton height={400} />}
			{ndaQuery.isSuccess && dealQuery.isSuccess && (
				<>
					{ndaQuery.data.activeNda ? (
						<CerebroStyledBox sx={{ marginTop: 5, marginBottom: 5 }}>
							<CerebroHeading size="h2" component="h2" sx={{ marginBottom: 4 }}>
								<FontAwesomeIcon icon={faCheck} size="sm" /> Your signed NDA is on file, effective
								<span>
									{' '}
									{DateUtils.FormatStringAsShortDate(
										ndaQuery.data.activeNda.effectiveDate?.toString()
									) ?? '--'}
								</span>
							</CerebroHeading>
							<CerebroLinkButton
								href={CerebroApiEndpoints.Deals.AbsoluteDownloadNda(guid, ndaQuery.data.activeNda.guid)}
								target="_blank"
								rel="noreferrer"
							>
								View Current NDA
							</CerebroLinkButton>{' '}
							{currentUser.isEmployeeUser && (
								<>
									<CerebroSecondaryButton sx={{ marginTop: 4 }} onClick={() => setUploadNda(true)}>
										Upload Mutual NDA
									</CerebroSecondaryButton>

									{ndaQuery.data.availableNdas.length > 0 && (
										<CerebroSecondaryButton
											sx={{ marginTop: 4 }}
											onClick={() => setAssociateNda(true)}
										>
											Associate Existing NDA
										</CerebroSecondaryButton>
									)}
								</>
							)}
						</CerebroStyledBox>
					) : (
						<CerebroStyledBox sx={{ marginTop: 5, marginBottom: 5 }}>
							{currentUser.isEmployeeUser ? (
								<>
									<CerebroHeading size="h2" component="h2" sx={{ marginBottom: 4 }}>
										The user has not signed an NDA with us.
									</CerebroHeading>
									<CerebroText component="p">
										Have them sign a mutual NDA so they can comfortably share their data with us or:
									</CerebroText>
									<CerebroSecondaryButton sx={{ marginTop: 4 }} onClick={() => setUploadNda(true)}>
										Upload Mutual NDA
									</CerebroSecondaryButton>
									{ndaQuery.data.availableNdas.length > 0 && (
										<CerebroSecondaryButton
											sx={{ marginTop: 4 }}
											onClick={() => setAssociateNda(true)}
										>
											Associate Existing NDA
										</CerebroSecondaryButton>
									)}
								</>
							) : (
								<>
									<CerebroHeading size="h2" component="h2" sx={{ marginBottom: 4 }}>
										Your information is secure and private.
									</CerebroHeading>
									<CerebroText component="p">
										Sign a mutual NDA so you can comfortably share your data with us.
									</CerebroText>
									<CerebroSecondaryButton sx={{ marginTop: 4 }} onClick={() => setSignNda(true)}>
										Execute Mutual NDA
									</CerebroSecondaryButton>
								</>
							)}
						</CerebroStyledBox>
					)}

					<UploadNdaDrawer
						open={uploadNda}
						dealGuid={guid}
						companyGuid={dealQuery.data.companyGuid}
						isContactParentCompany={ndaQuery.data.isContactParentCompany}
						companies={ndaQuery.data.companies}
						onClose={() => setUploadNda(false)}
					/>
					<SignNdaDrawer
						open={signNda}
						dealGuid={guid}
						companyGuid={dealQuery.data.companyGuid}
						onClose={() => setSignNda(false)}
						currentUser={currentUser}
					/>
					<AssociateCompanyNda
						open={associateNda}
						dealGuid={guid}
						availableNdas={ndaQuery.data.availableNdas}
						onClose={() => setAssociateNda(false)}
					/>
				</>
			)}
		</CerebroPageContainer>
	);
};
